import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  List,
  ListItem,
  Link,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

const ViewFilesModal = ({ open, onClose, bondDetails }) => {
  const [selectedFiles, setSelectedFiles] = useState([]); // Files selected but not yet uploaded
  const [pages, setPages] = useState([]); // Existing files
  const [fileCount, setFileCount] = useState(0); // Count of files
  const [isUploading, setIsUploading] = useState(false); // Spinner state for file upload
  const [isLoading, setIsLoading] = useState(false); // Loading existing files

  useEffect(() => {
    if (open && bondDetails) {
      fetchPages();
      fetchFileCount();
    }
  }, [open]);

  const fetchPages = async () => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;

    try {
      const response = await fetch(
        `${baseURL}PostingTransferBond/${bondDetails.transferBondTransactionID}/pages`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch pages.');
      }

      const data = await response.json();
      setPages(data.pages);
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while fetching files.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFileCount = async () => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;

    try {
      const response = await fetch(
        `${baseURL}PostingTransferBond/${bondDetails.transferBondTransactionID}/FileCount`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to check uploaded files.');
      }

      const data = await response.json();
      setFileCount(data.count);
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while checking uploaded files.');
    }
  };

  const generatePageLink = (page) => {
    return `https://bss-api.azure-api.net/imgnow//docPage/${bondDetails.imageNowId}/page/${page.id}/extension/${page.extension.toLowerCase()}?subscription-key=0f6da358b99f46fdb5d14d6d9b914684`;
  };

  // Handle file selection
  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, ...files]);
  };

  // Handle file uploads
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload.');
      return;
    }

    setIsUploading(true); // Start spinner

    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    const formData = new FormData();

    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await fetch(
        `${baseURL}PostingTransferBond/${bondDetails.transferBondTransactionID}/UploadFiles`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload files.');
      }

      toast.success('Files uploaded successfully.');
      setSelectedFiles([]); // Clear selected files
      await fetchPages(); // Refresh pages
      await fetchFileCount(); // Refresh file count
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while uploading files.');
    } finally {
      setIsUploading(false); // Stop spinner
    }
  };

  // Handle removing selected files
  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prevSelectedFiles) =>
      prevSelectedFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>View and Upload Additional Files</DialogTitle>
      <DialogContent>
        {/* Existing Documents */}
        {isLoading ? (
          <CircularProgress />
        ) : pages.length > 0 ? (
          <List>
            {pages.map((page, index) => (
              <ListItem key={page.id}>
                <Link
                  href={generatePageLink(page)}
                  target="_blank"
                  rel="noopener"
                >
                  Document #{index + 1} ({page.extension.toLowerCase()})
                </Link>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography>No files available.</Typography>
        )}

        {/* Current File Count */}
        <Typography variant="subtitle1" sx={{ flexGrow: 1, mt: 2 }}>
          File Count: <b>{fileCount}</b>
        </Typography>

        {/* File Selection and Upload */}
        <Box sx={{ mb: 2, mt: 2 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            Choose Files
            <input
              type="file"
              hidden
              multiple
              accept="image/*,application/pdf"
              onChange={handleFileSelection}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFileUpload}
            disabled={isUploading || selectedFiles.length === 0} // Disable upload button while uploading
            sx={{ ml: 2 }}
          >
            {isUploading ? (
              <CircularProgress size={24} /> // Spinner shown while uploading
            ) : (
              'Upload Files'
            )}
          </Button>
        </Box>

        {/* List Selected Files */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Files to Upload:</Typography>
            {selectedFiles.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mt: 1,
                }}
              >
                <CheckCircleIcon sx={{ color: 'green', mr: 1 }} />
                <Typography>{file.name}</Typography>
                <IconButton
                  aria-label="remove file"
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                  sx={{ ml: 2 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewFilesModal;
